import styles from './dropdown.module.scss';
import {
  FieldValues,
  useController,
  UseControllerProps
} from "react-hook-form";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import arrowIcon from '../../images/arrow.svg';
import { Popup } from "../popup/popup";
import { useTranslation } from "react-i18next";

export type DropdownItem = {
  id: number | string | null;
  name: string | JSX.Element | JSX.Element[];
  title: string;
  disabled?: boolean;
};

type DropdownProps<T extends FieldValues> = {
  items: DropdownItem[];
  showCleanField?: boolean;
  disabled?: boolean;
  fontPopupClass?: string;
  selectedItemClass?: string;
  isOpenProp?: boolean;
  setIsOpenProp?: (isOpen: boolean) => void;
  error?: string;
  isInput?: boolean;
} & UseControllerProps<T>;

export const Dropdown = <T extends FieldValues>({ items, isInput, showCleanField, control, disabled, name, fontPopupClass, selectedItemClass, isOpenProp, setIsOpenProp, error }: DropdownProps<T>) => {
  const { t } = useTranslation();

  const dropdownContainer = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [localItems, setLocalItems] = useState<Array<DropdownItem>>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [isOpen, setIsOpen] = useState<boolean>(!!isOpenProp);

  const {
    field: { value: currentValue, onChange },
  } = useController({
    name,
    control
  });

  useEffect(() => {
    const result = showCleanField ? [{ id: null, name: "", title: "" }, ...items] : [...items];
    setLocalItems(result);
  }, [items, showCleanField]);

  const areValuesEqual = useCallback((a: unknown, b: unknown): boolean => {
    return a === b || (a?.toString() === b?.toString());
  }, []);

  const setDefaultIndex = useCallback((currentValue: unknown) => {
    if (localItems.length !== 0) {
      const defaultIndex = localItems.findIndex(item => areValuesEqual(item.id, currentValue));
      if (defaultIndex > -1) {
        setSelectedIndex(defaultIndex);
        if (!areValuesEqual(currentValue, localItems[defaultIndex].id)) {
          onChange(localItems[defaultIndex].id);
        }
      }
    }
  }, [localItems, setSelectedIndex, onChange, areValuesEqual]);

  useEffect(() => {
    if (!isInput) {
      if (!!currentValue) {
        setDefaultIndex(currentValue);
      } else {
        if (localItems.length > 0) {
          setSelectedIndex(0);
          onChange(localItems[0].id);
        }
      }
    } else {
      if (!!currentValue) {
        setDefaultIndex(currentValue);
      }
    }
  }, [localItems, isInput, currentValue, setSelectedIndex, setDefaultIndex]);

  useEffect(() => {
    if (inputRef.current && currentValue) {
      inputRef.current.value = items.find(item => item.id === currentValue)?.title || "";
    }
  }, [currentValue, items]);

  useEffect(() => {
    if (setIsOpenProp) {
      setIsOpenProp(isOpen);
    }
  }, [isOpen, setIsOpenProp]);

  useEffect(() => {
    setIsOpen(!!isOpenProp);
  }, [isOpenProp]);

  const onClickDropdown = (): void => {
    if (!isOpen && !disabled) {
      setIsOpen(true);
    }
  };

  const onClosePopup = useCallback((e?: MouseEvent): void => {
    if (!isInput || !e || !dropdownContainer.current || !dropdownContainer.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, [setIsOpen, isInput]);

  const onClickItem = (itemIndex: number): void => {
    if (!localItems[itemIndex].disabled && selectedIndex !== itemIndex) {
      onChange(localItems[itemIndex].id);
      setSelectedIndex(itemIndex);
      if (!!inputRef.current) {
        inputRef.current.value = localItems[itemIndex].title;
      }
    }
    setIsOpen(false);
  };

  const onChangeInput = useCallback(() => {
    const value = inputRef.current?.value || "";
    const filteredItems = items.filter(item => item.title.toLowerCase().includes(value.toLowerCase()));
    if (filteredItems.length === 1 && value === filteredItems[0].title) {
      onChange(filteredItems[0].id);
    } else {
      setSelectedIndex(-1);
      onChange(null);
    }
    setIsOpen(true);
    setLocalItems(filteredItems);
  }, [setLocalItems, onChange, setIsOpen, items]);

  const getItemClassName = useCallback((index?: number): string => {
    let result = styles.item;

    if (index === selectedIndex) {
      result += ` ${styles.selected}`;
    }

    if (index == null) {
      result += ` ${styles.noResult}`;
    }

    if (fontPopupClass) {
      result += ` ${fontPopupClass}`;
    }

    return result;
  }, [selectedIndex, fontPopupClass]);

  const classContainer = useMemo(() => {
    let result = styles.container;
    if (disabled) {
      result += ` ${styles.disabled}`;
    }
    if (error && !disabled) {
      result += ` ${styles.error}`;
    }
    return result;
  }, [disabled, error]);

  return (
    <div className={classContainer}
         onMouseDown={onClickDropdown}
         ref={dropdownContainer}>
      {isInput && (
        <input ref={inputRef}
               onChange={onChangeInput}
               className={selectedItemClass || ""}
               disabled={disabled}/>
      )}
      { !isInput && (
        <div className={`${styles.selectedItem} ${selectedItemClass || ""}`} title={localItems[selectedIndex]?.title}>
          {localItems[selectedIndex]?.name}
        </div>
      )}

      <img className={styles.arrow} src={arrowIcon} alt=""/>
      {error && !disabled && <p className={styles.errorMessage}>{error}</p>}
      <Popup
        parent={dropdownContainer.current}
        width={dropdownContainer.current?.offsetWidth}
        isShow={isOpen}
        onClose={onClosePopup}
        child={
          <div className={styles.popupContainer}>
            <div className={styles.items}>
              {localItems.map((item, index) => (
                <div key={item.id}
                     className={getItemClassName(index)}
                     title={item.title}
                     onClick={() => onClickItem(index)}>
                  {item.name}
                </div>
              ))}
              {isInput && localItems.length === 0 && (
                <div className={getItemClassName()}
                     title={t("common.noResult")}>
                  {t("common.noResult")}
                </div>
              )}
            </div>
          </div>
        }
      />
    </div>
  );
};
